import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Link } from 'react-router-dom'
import { offerSchema } from '@/utils/validation'
import { createOffer } from '@/services/api/offers'
import { useAuth } from '@/hooks/useAuth'
import { useUIStore } from '@/store/uiStore'
import { Input } from '@/components/common/Input'
import { Button } from '@/components/common/Button'
import { DollarSign, ArrowRight } from 'lucide-react'
import type { Listing } from '@/types/listing'

type OfferFormData = {
  amount: number
  message?: string
}

interface MakeOfferSectionProps {
  listing: Listing
}

export function MakeOfferSection({ listing }: MakeOfferSectionProps) {
  const { user } = useAuth()
  const { showToast, openModal } = useUIStore()
  const [submitting, setSubmitting] = useState(false)
  const [offerSent, setOfferSent] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<OfferFormData>({
    resolver: zodResolver(offerSchema),
  })

  const onSubmit = async (data: OfferFormData) => {
    if (!user) {
      openModal('login')
      return
    }
    setSubmitting(true)
    try {
      await createOffer(
        listing.id,
        listing.title,
        user.uid,
        user.displayName || 'Buyer',
        listing.sellerId,
        listing.sellerName || 'Seller',
        data.amount,
        data.message
      )
      showToast('Offer sent!')
      setOfferSent(true)
    } catch {
      showToast('Failed to send offer', 'error')
    } finally {
      setSubmitting(false)
    }
  }

  if (offerSent) {
    return (
      <div className="card p-4 bg-green-50 border-green-200">
        <div className="flex items-center gap-2 text-green-700 mb-3">
          <DollarSign className="w-5 h-5 flex-shrink-0" />
          <span className="font-medium">Offer sent! The seller will be notified.</span>
        </div>
        <Link
          to="/offers"
          className="inline-flex items-center gap-1.5 text-sm text-green-700 hover:text-green-800 font-medium underline"
        >
          Track your offers
          <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    )
  }

  return (
    <div className="card p-4">
      <h3 className="font-semibold mb-3 flex items-center gap-2">
        <DollarSign className="w-5 h-5 text-primary-500" />
        Make an Offer
      </h3>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
        <Input
          label="Your Offer (NZD)"
          type="number"
          placeholder="0.00"
          error={errors.amount?.message}
          {...register('amount', { valueAsNumber: true })}
        />
        <div>
          <label className="label">Message (optional)</label>
          <textarea
            placeholder="Include a message with your offer..."
            className="input min-h-[60px]"
            {...register('message')}
          />
        </div>
        <Button type="submit" fullWidth loading={submitting}>
          Send Offer
        </Button>
      </form>
    </div>
  )
}
